import styled from '@emotion/styled';
import { Divider, Loader, SearchInput, Text, tokens, useTippy } from '@hotjar/hotsauce';
import { NavDropdown, NavList } from '@hotjar/navigation/src/components/NavDropdown';
import { Position } from '@hotjar/navigation/src/events/Position';
import type { NavigationEvent } from '@hotjar/navigation/src/events/types';
import type {
  DemoSiteSelectorExperiment,
  EventEmitter,
  Organization,
  Site,
} from '@hotjar/navigation/src/types';
import { debounce } from '@hotjar/navigation/src/utils/debounce';
import type { ChangeEvent, ReactElement } from 'react';
import { useEffect } from 'react';
import { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigation } from '@hotjar/navigation/src/hooks/useNavigation';

import { AddNewSite } from './AddNewSite';
import { filterData } from './filterOrgsAndSite';
import { OrganizationSiteList } from './OrganizationSiteList';
import { SelectedSite } from './SelectedSite';

interface SiteSelectorProps {
  className?: string;
  emitter: EventEmitter;
  currentSite: Site | null;
  getOrgsAndSites: (userId: number) => Promise<Organization[]>;
  currentRouteConfig?: {
    path?: string;
    name: string;
  };
  userId: number;
  demoSiteSelectorConfig: DemoSiteSelectorExperiment;
}

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: ${tokens.spacer.x1};
`;

const StyledDropdown = styled(NavDropdown)`
  width: 310px;
`;

const SearchContainer = styled.div`
  padding: ${tokens.spacer.x1pt5} ${tokens.spacer.x1pt5} ${tokens.spacer.x0};
  flex-shrink: 0;
`;

const DropdownContent = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 438px; // dropdown max-height minus border
`;

const ListContainer = styled(NavList)`
  flex-grow: 1;
  padding: 0;
  overflow-y: auto;
`;

const ActionContainer = styled(NavList)`
  padding: 0 0 ${tokens.spacer.x1};
  flex-shrink: 0;
`;

const StyledText = styled(Text)`
  padding: 0 ${tokens.spacer.x1};
`;

const eventPosition: NavigationEvent = Object.freeze({ position: Position.siteSelector });

export const SiteSelector = ({
  className,
  emitter,
  currentSite,
  getOrgsAndSites,
  currentRouteConfig,
  userId,
  demoSiteSelectorConfig,
}: SiteSelectorProps): JSX.Element => {
  const { t } = useTranslation('navigation');
  const { routes } = useNavigation();
  const tippyInstance = useTippy();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [query, setQuery] = useState<string>('');

  const [siteHrefRoute, setSiteHrefRoute] = useState<string>('');
  const [siteHrefArgs, setSiteHrefArgs] = useState<Record<string, string>>({});

  const [isDropdownMounted, setDropdownMounted] = useState(false);

  const { demoSiteOrg, addToDemoSite, isAddingToDemoSite } = demoSiteSelectorConfig;

  const setOrgsAndSitesWithDemoSite = useCallback(
    (orgsAndSites: Organization[]) => {
      if (!orgsAndSites.find((org) => org.id === demoSiteOrg.id)) {
        return setOrganizations([...orgsAndSites, demoSiteOrg]);
      }

      return setOrganizations(orgsAndSites);
    },
    [demoSiteOrg],
  );

  const handleOnSelect = async (site: Site) => {
    if (site.id === demoSiteOrg.sites[0].id) {
      await addToDemoSite();
    }
    tippyInstance.close();
    emitter.emit('selectedSite', {
      position: Position.siteSelector,
      site,
    });
  };

  useEffect(() => {
    let isSiteSelectorMounted = true;

    async function fetchOrgsAndSites() {
      const orgsAndSites = await getOrgsAndSites(userId);

      if (isSiteSelectorMounted) {
        setOrgsAndSitesWithDemoSite(orgsAndSites);
      }
    }

    if (isDropdownMounted) {
      fetchOrgsAndSites();
    }

    return () => {
      isSiteSelectorMounted = false;
    };
  }, [getOrgsAndSites, isDropdownMounted, userId, setOrgsAndSitesWithDemoSite]);

  useEffect(() => {
    // reset additional args
    setSiteHrefArgs({});

    setSiteHrefRoute(routes.OVERVIEW.BASE.path);
  }, [currentRouteConfig, routes]);

  const filteredOrganizations = filterData(query, organizations);

  const organizationList: ReactElement[] = [];
  filteredOrganizations.forEach((organization, index) => {
    if (index !== 0) {
      organizationList.push(<Divider key={`divider-${organization.id}`} />);
    }

    organizationList.push(
      <OrganizationSiteList
        key={organization.id}
        organization={organization}
        selectedSiteId={currentSite?.id}
        siteHrefRoute={siteHrefRoute}
        siteHrefArgs={siteHrefArgs}
        onSelect={handleOnSelect}
        demoSiteItem={{ isLoading: isAddingToDemoSite, id: demoSiteOrg.sites[0].id }}
      />,
    );
  });

  const handleSearch = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      emitter.emit('searchedSites', {
        position: Position.siteSelector,
        searchTerm: event.target.value,
      });
      setQuery(event.target.value);
    },
    [emitter],
  );

  const debouncedChangeHandler = useMemo(() => debounce(300, handleSearch), [handleSearch]);

  return (
    <Container className={className} data-testid="site-selector">
      <StyledDropdown
        trigger={<SelectedSite site={currentSite} />}
        appendTo="parent"
        onMount={() => setDropdownMounted(true)}
        onTrigger={() => {
          emitter.emit('clickedSiteSelector', { position: Position.primaryNavigation });
        }}
        onCreate={tippyInstance.set}
      >
        <DropdownContent>
          <SearchContainer>
            <SearchInput
              placeholder={t('SiteSelector.searchPlaceholder')}
              onChange={debouncedChangeHandler}
              onFocus={() => {
                emitter.emit('focusedSiteSelectorSearch', eventPosition);
              }}
            />
          </SearchContainer>

          {organizations.length === 0 && <Loader />}

          {organizations.length > 0 && (
            <ListContainer>
              {organizationList.length > 0 ? (
                organizationList
              ) : (
                <StyledText textColor="text_disabled">{t('SiteSelector.emptyResults')}</StyledText>
              )}
            </ListContainer>
          )}

          <Divider />

          <ActionContainer>
            <AddNewSite
              onClick={() => {
                tippyInstance.close();
                emitter.emit('clickedAddNewSite', eventPosition);
              }}
            />
          </ActionContainer>
        </DropdownContent>
      </StyledDropdown>
    </Container>
  );
};
